import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate, downloadFile, ApiError } from '../shared/api';
import { formatMoney, formatNumber, formatDate } from '../shared/formatting';

type Locale = 'hr' | 'en';

export type PgRunStatus = 'draft' | 'confirmed' | 'sent' | 'received' | 'cancelled';

type Candidate = {
    product_id: number;
    sku: string;
    name_hr: string;
    supplier_partner_id: number | null;
    supplier_name: string | null;
    last_purchase_price_eur: number | null;
};

type RunLine = {
    id: number;
    warehouse_code: string | null;
    line_type?: 'supplier' | 'transfer';
    source_warehouse_code?: string | null;
    weight_class_code: string | null;
    product_id: number | null;
    sku: string | null;
    item_name: string | null;
    supplier_partner_id: number | null;
    supplier_name: string | null;
    qty: number | string | null;
    unit_price_eur: number | string | null;
    total_eur: number | string | null;
    fine_g: number | string | null;
    priority: 'urgent' | 'normal';
    excluded: boolean;
    exclude_reason: string | null;
    candidates: Candidate[];
    replaced_manually: boolean;
};

type Group = { lines: number; qty: number; fine_g: number; total_eur: number };

type Valuation = {
    metal: string;
    totals: Group & {
        excluded_lines: number;
        missing_price: number;
        urgent_lines: number;
        spot_per_gram_eur: number | null;
        spot_value_eur: number | null;
        premium_eur: number | null;
        transfer_qty?: number;
        transfer_eur?: number;
        supplier_qty?: number;
        supplier_eur?: number;
    };
    by_supplier: (Group & { supplier_partner_id: number | null; supplier_name: string | null })[];
    by_warehouse: (Group & { warehouse_code: string | null })[];
};

type RunEvent = {
    id: number;
    event: 'status' | 'line_edit' | 'in_transit' | 'revaluation';
    from_status: string | null;
    to_status: string | null;
    line_id: number | null;
    note: string | null;
    created_at: string;
    user_name: string | null;
};

export type PgRun = {
    id: number;
    public_code: string;
    mode: 'pg1' | 'pg2';
    status: PgRunStatus;
    generated_at: string;
    status_changed_at: string | null;
    note: string | null;
    created_by_name: string | null;
    filters: Record<string, unknown> | null;
    lines: RunLine[];
    allowed_transitions: PgRunStatus[];
    valuation: Valuation;
    events: RunEvent[];
};

const STATUS_HR: Record<PgRunStatus, string> = { draft: 'nacrt', confirmed: 'potvrđen', sent: 'poslan', received: 'primljen', cancelled: 'storniran' };
const STATUS_EN: Record<PgRunStatus, string> = { draft: 'draft', confirmed: 'confirmed', sent: 'sent', received: 'received', cancelled: 'cancelled' };
const STATUS_BG: Record<PgRunStatus, string> = { draft: '#f3f4f6', confirmed: '#e0e7ff', sent: '#dcfce7', received: '#d1fae5', cancelled: '#fee2e2' };

const num = (v: number | string | null | undefined): number | null => (v === null || v === undefined || v === '' ? null : Number(v));

/**
 * Drugi ekran 40.42 (N5, TASK-40-61, odluka Vedran 25.09.2026): nakon
 * spremanja PG1/PG2 prijedloga run se otvara ovdje, kao u 40.4 - valorizacija,
 * ispis, XLSX. Uz to N6: status runa (nacrt, potvrden, poslan, storniran) i
 * rucna zamjena kandidata po retku dok je run u nacrtu.
 *
 * Redoslijed na ekranu prati posao: stanje i sljedeci korak gore, brojevi
 * odmah ispod, pa retci, pa trag. Sve izmjene idu na server i ekran se puni
 * iz odgovora, da ispis i XLSX uvijek pokazuju isto sto i baza.
 */
export function PgRunView({ locale, token, runId, onBack }: { locale: Locale; token: string | null; runId: number; onBack: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const statusLabel = (s: PgRunStatus | string | null) => (s ? (locale === 'en' ? STATUS_EN : STATUS_HR)[s as PgRunStatus] ?? s : '');

    const [run, setRun] = useState<PgRun | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [statusNote, setStatusNote] = useState('');

    useEffect(() => {
        setError(null);
        api<PgRun>(`/gold-cm-planner/runs/${runId}`, token)
            .then(setRun)
            .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje runa nije uspjelo.', 'Loading run failed.')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runId, token]);

    const call = useCallback(async (fn: () => Promise<PgRun>) => {
        setBusy(true);
        setError(null);
        try {
            setRun(await fn());
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Izmjena nije uspjela.', 'Update failed.'));
        } finally {
            setBusy(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setStatus = (status: PgRunStatus) => {
        if (status === 'cancelled' && !window.confirm(t('Stornirati run?', 'Cancel this run?'))) return;
        void call(() => mutate<PgRun>(`/gold-cm-planner/runs/${runId}/status`, token, 'POST', { status, note: statusNote || null })).then(() => setStatusNote(''));
    };

    const editLine = (lineId: number, body: Record<string, unknown>) =>
        void call(() => mutate<PgRun>(`/gold-cm-planner/runs/${runId}/lines/${lineId}`, token, 'PATCH', body));

    const exportXlsx = async () => {
        if (!run) return;
        try {
            await downloadFile(`/gold-cm-planner/runs/${run.id}/export.xlsx`, token, `${run.public_code}.xlsx`);
        } catch {
            setError(t('Export nije uspio.', 'Export failed.'));
        }
    };

    if (!run) {
        return (
            <div>
                <button type="button" className="secondary compact" onClick={onBack}>← {t('Natrag', 'Back')}</button>
                {error ? <p style={{ color: '#b91c1c' }}>{error}</p> : <p className="muted">{t('Učitavam run…', 'Loading run…')}</p>}
            </div>
        );
    }

    const draft = run.status === 'draft';
    const pg2 = run.mode === 'pg2';
    const v = run.valuation.totals;
    const actionLabel: Record<PgRunStatus, string> = {
        draft: t('Vrati u nacrt', 'Back to draft'),
        confirmed: t('Potvrdi', 'Confirm'),
        sent: t('Označi poslano', 'Mark as sent'),
        received: t('Roba primljena', 'Goods received'),
        cancelled: t('Storniraj', 'Cancel run'),
    };

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <button type="button" className="secondary compact" onClick={onBack}>← {t('Novi prijedlog', 'New proposal')}</button>
                <h2 style={{ margin: 0 }}>{run.public_code}</h2>
                <span style={{ padding: '2px 10px', borderRadius: 4, background: STATUS_BG[run.status], fontWeight: 600 }}>{statusLabel(run.status)}</span>
                <span className="muted">
                    {run.mode.toUpperCase()} · {formatDate(run.generated_at, locale)} {run.generated_at.slice(11, 16)}
                    {run.created_by_name ? ` · ${run.created_by_name}` : ''}
                    {run.note ? ` · ${run.note}` : ''}
                </span>
                <span style={{ flex: 1 }} />
                <button type="button" onClick={() => printRun(run, locale)}>{t('Ispis', 'Print')}</button>
                <button type="button" className="secondary" onClick={() => void exportXlsx()}>XLSX</button>
            </div>

            {run.allowed_transitions.length > 0 ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
                    {run.allowed_transitions.map((s) => (
                        <button key={s} type="button" className={s === 'cancelled' || s === 'draft' ? 'secondary' : ''} disabled={busy} onClick={() => setStatus(s)}>
                            {actionLabel[s]}
                        </button>
                    ))}
                    <label>{t('Napomena uz promjenu', 'Note for the change')}<br />
                        <input value={statusNote} onChange={(e) => setStatusNote(e.target.value)} style={{ width: 280 }} />
                    </label>
                    {draft ? <span className="muted">{t('U nacrtu se retci mogu mijenjati; potvrda ih zaključava.', 'Lines are editable in draft; confirming locks them.')}</span> : null}
                </div>
            ) : null}

            {error ? <p style={{ color: '#b91c1c' }}>{error}</p> : null}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginBottom: '1rem' }}>
                <Stat label={t('Redaka', 'Lines')} value={`${v.lines}${v.excluded_lines ? ` (+${v.excluded_lines} ${t('isklj.', 'excl.')})` : ''}`} />
                {pg2 ? <Stat label={t('Komada', 'Pieces')} value={formatNumber(v.qty, locale)} /> : <Stat label={t('Hitnih', 'Urgent')} value={String(v.urgent_lines)} />}
                {pg2 && v.transfer_qty ? <Stat label={t('Iz 211 (kom / EUR)', 'From 211 (qty / EUR)')} value={`${formatNumber(v.transfer_qty, locale)} / ${formatMoney(v.transfer_eur ?? 0, locale)}`} /> : null}
                {pg2 && v.transfer_qty ? <Stat label={t('Dobavljačima (kom / EUR)', 'To suppliers (qty / EUR)')} value={`${formatNumber(v.supplier_qty ?? 0, locale)} / ${formatMoney(v.supplier_eur ?? 0, locale)}`} /> : null}
                <Stat label={t('Fino (g)', 'Fine (g)')} value={formatNumber(v.fine_g, locale)} />
                <Stat label={pg2 ? t('Vrijednost po zadnjoj nabavnoj', 'Value at last purchase') : t('Knjigovodstvena vrijednost', 'Book value')} value={formatMoney(v.total_eur, locale)} />
                {v.spot_value_eur !== null ? <Stat label={`${t('Po spotu', 'At spot')} (${formatMoney(v.spot_per_gram_eur ?? 0, locale)}/g)`} value={formatMoney(v.spot_value_eur, locale)} /> : null}
                {v.premium_eur !== null ? <Stat label={t('Razlika prema spotu', 'Difference to spot')} value={formatMoney(v.premium_eur, locale)} /> : null}
            </div>
            {v.missing_price > 0 ? <p className="muted">{v.missing_price} {t('redaka bez cijene nije u vrijednosti.', 'lines without price are not valued.')}</p> : null}

            {pg2 && run.valuation.by_supplier.length > 0 ? (
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: '1rem' }}>
                    <GroupTable
                        title={t('Po dobavljaču', 'By supplier')}
                        rows={run.valuation.by_supplier.map((g) => ({ key: String(g.supplier_partner_id ?? '-'), label: g.supplier_name ?? String(g.supplier_partner_id ?? '-'), ...g }))}
                        locale={locale}
                        t={t}
                    />
                    <GroupTable
                        title={t('Po poslovnici', 'By branch')}
                        rows={run.valuation.by_warehouse.map((g) => ({ key: g.warehouse_code ?? '-', label: g.warehouse_code ?? '-', ...g }))}
                        locale={locale}
                        t={t}
                    />
                </div>
            ) : null}

            <div style={{ overflowX: 'auto' }}>
                <table className="table">
                    <thead>
                        <tr>
                            <th>{t('Uklj.', 'Incl.')}</th>
                            {pg2 ? <th>{t('Poslovnica', 'Branch')}</th> : null}
                            {pg2 ? <th>{t('Apoen', 'Denom.')}</th> : null}
                            <th>{pg2 ? t('Artikl / dobavljač', 'Item / supplier') : t('Artikl', 'Item')}</th>
                            {pg2 ? <th>{t('Kom', 'Qty')}</th> : null}
                            <th>{t('Fino g', 'Fine g')}</th>
                            {pg2 ? <th>{t('Jed. cijena', 'Unit price')}</th> : null}
                            <th>EUR</th>
                            {!pg2 ? <th>{t('Hitno', 'Urgent')}</th> : null}
                        </tr>
                    </thead>
                    <tbody>
                        {run.lines.map((l) => (
                            <tr key={l.id} style={l.excluded ? { opacity: 0.45 } : undefined}>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={!l.excluded}
                                        disabled={!draft || busy || (pg2 && l.product_id === null)}
                                        onChange={() => editLine(l.id, { excluded: !l.excluded })}
                                        title={l.exclude_reason ?? ''}
                                    />
                                </td>
                                {pg2 ? <td>{l.line_type === 'transfer' ? `${l.source_warehouse_code ?? '211'} → ` : ''}{l.warehouse_code ?? ''}</td> : null}
                                {pg2 ? <td>{l.weight_class_code ?? ''}</td> : null}
                                <td>
                                    {pg2 && draft && l.candidates.length > 0 ? (
                                        <select
                                            value={l.product_id ?? ''}
                                            disabled={busy}
                                            onChange={(e) => editLine(l.id, { product_id: Number(e.target.value) })}
                                            style={{ maxWidth: 360 }}
                                        >
                                            {l.product_id === null ? <option value="">{t('odaberi artikl', 'choose item')}</option> : null}
                                            {l.candidates.map((c) => (
                                                <option key={c.product_id} value={c.product_id}>
                                                    {c.sku} · {c.name_hr} · {c.supplier_name ?? '-'}{c.last_purchase_price_eur != null ? ` · ${formatMoney(c.last_purchase_price_eur, locale)}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    ) : l.sku ? (
                                        <>{l.sku} · {l.item_name}{pg2 && l.supplier_name ? <span className="muted"> · {l.supplier_name}</span> : null}</>
                                    ) : (
                                        <em>{t('bez kandidata', 'no candidate')}</em>
                                    )}
                                    {l.replaced_manually ? <span className="muted" title={t('Ručno zamijenjeno', 'Replaced manually')}> ✎</span> : null}
                                </td>
                                {pg2 ? (
                                    <td>
                                        {draft ? (
                                            <QtyInput value={num(l.qty) ?? 0} disabled={busy} onCommit={(q) => editLine(l.id, { qty: q })} />
                                        ) : formatNumber(num(l.qty) ?? 0, locale)}
                                    </td>
                                ) : null}
                                <td>{num(l.fine_g) !== null ? formatNumber(num(l.fine_g) as number, locale) : ''}</td>
                                {pg2 ? <td>{num(l.unit_price_eur) !== null ? formatMoney(num(l.unit_price_eur) as number, locale) : ''}</td> : null}
                                <td>{num(l.total_eur) !== null ? formatMoney(num(l.total_eur) as number, locale) : ''}</td>
                                {!pg2 ? <td>{l.priority === 'urgent' ? t('da', 'yes') : ''}</td> : null}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <details style={{ marginTop: '1rem' }}>
                <summary>{t('Trag runa', 'Run history')} ({run.events.length})</summary>
                <table className="table" style={{ marginTop: 4 }}>
                    <tbody>
                        {run.events.map((e) => (
                            <tr key={e.id}>
                                <td>{formatDate(e.created_at, locale)} {e.created_at.slice(11, 16)}</td>
                                <td>{e.user_name ?? ''}</td>
                                <td>
                                    {e.event === 'status'
                                        ? `${e.from_status ? `${statusLabel(e.from_status)} → ` : ''}${statusLabel(e.to_status)}`
                                        : e.event === 'in_transit'
                                          ? t('provjera: roba još putuje', 'check: goods still in transit')
                                          : e.event === 'revaluation'
                                            ? t('ispravak vrijednosti', 'revaluation')
                                            : `${t('izmjena retka', 'line edit')} #${e.line_id}`}
                                </td>
                                <td>{e.note ?? ''}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </details>
        </div>
    );
}

function Stat({ label, value }: { label: string; value: string }) {
    return (
        <div className="card" style={{ padding: '0.6rem 0.8rem' }}>
            <div className="muted" style={{ fontSize: 12 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{value}</div>
        </div>
    );
}

function GroupTable({ title, rows, locale, t }: {
    title: string;
    rows: (Group & { key: string; label: string })[];
    locale: Locale;
    t: (hr: string, en: string) => string;
}) {
    return (
        <div style={{ flex: '1 1 320px' }}>
            <strong>{title}</strong>
            <table className="table" style={{ marginTop: 4 }}>
                <thead>
                    <tr><th></th><th>{t('Redaka', 'Lines')}</th><th>{t('Kom', 'Qty')}</th><th>{t('Fino g', 'Fine g')}</th><th>EUR</th></tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.key}>
                            <td>{r.label}</td>
                            <td>{r.lines}</td>
                            <td>{formatNumber(r.qty, locale)}</td>
                            <td>{formatNumber(r.fine_g, locale)}</td>
                            <td>{formatMoney(r.total_eur, locale)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** Kolicina se salje tek na blur/Enter, ne na svaki pritisak tipke. */
function QtyInput({ value, disabled, onCommit }: { value: number; disabled: boolean; onCommit: (q: number) => void }) {
    const [text, setText] = useState(String(value));
    useEffect(() => setText(String(value)), [value]);
    const commit = () => {
        const q = Number(text.replace(',', '.'));
        if (Number.isFinite(q) && q >= 0 && q !== value) onCommit(q);
        else setText(String(value));
    };
    return (
        <input
            value={text}
            disabled={disabled}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            style={{ width: 70 }}
        />
    );
}

const esc = (s: unknown) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

/**
 * Ispis runa kao zaseban dokument u novom prozoru: crno-bijelo, Arial, A4
 * polozeno, bez navigacije aplikacije. Iskljuceni retci se ne ispisuju.
 */
function printRun(run: PgRun, locale: Locale) {
    const w = window.open('', '_blank', 'width=1100,height=800');
    if (!w) return;
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const eur = (v: number | string | null) => (num(v) !== null ? formatMoney(num(v) as number, locale) : '');
    const n = (v: number | string | null) => (num(v) !== null ? formatNumber(num(v) as number, locale) : '');
    const pg2 = run.mode === 'pg2';
    const v = run.valuation.totals;
    const lines = run.lines.filter((l) => !l.excluded);
    const status = (locale === 'en' ? STATUS_EN : STATUS_HR)[run.status];

    const head = pg2
        ? [t('Poslovnica', 'Branch'), t('Apoen', 'Denom.'), 'SKU', t('Naziv', 'Name'), t('Dobavljač', 'Supplier'), t('Kom', 'Qty'), t('Fino g', 'Fine g'), t('Jed. cijena', 'Unit price'), 'EUR']
        : ['SKU', t('Naziv', 'Name'), t('Fino g', 'Fine g'), 'EUR', t('Hitno', 'Urgent')];
    const body = lines.map((l) => (pg2
        ? [`${l.line_type === 'transfer' ? `${l.source_warehouse_code ?? '211'} → ` : ''}${l.warehouse_code ?? ''}`, l.weight_class_code, l.sku, l.item_name, l.supplier_name, n(l.qty), n(l.fine_g), eur(l.unit_price_eur), eur(l.total_eur)]
        : [l.sku, l.item_name, n(l.fine_g), eur(l.total_eur), l.priority === 'urgent' ? t('da', 'yes') : '']));
    const numCols = pg2 ? [5, 6, 7, 8] : [2, 3];

    const groupHtml = (title: string, rows: (Group & { label: string })[]) => rows.length === 0 ? '' : `
<h3>${esc(title)}</h3>
<table><thead><tr><th></th><th class="r">${esc(t('Redaka', 'Lines'))}</th><th class="r">${esc(t('Kom', 'Qty'))}</th><th class="r">${esc(t('Fino g', 'Fine g'))}</th><th class="r">EUR</th></tr></thead>
<tbody>${rows.map((r) => `<tr><td>${esc(r.label)}</td><td class="r">${r.lines}</td><td class="r">${esc(n(r.qty))}</td><td class="r">${esc(n(r.fine_g))}</td><td class="r">${esc(eur(r.total_eur))}</td></tr>`).join('')}</tbody></table>`;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(run.public_code)}</title>
<style>
@page { size: A4 landscape; margin: 12mm; }
@media print { .no-print { display: none !important; } }
body { font-family: Arial, sans-serif; font-size: 9pt; color: #000; margin: 0; padding: 8mm; }
h1 { font-size: 14pt; margin: 0 0 2mm; }
h3 { font-size: 10pt; margin: 5mm 0 1mm; }
.meta { margin-bottom: 4mm; }
table { border-collapse: collapse; width: 100%; }
th, td { border: 0.5pt solid #000; padding: 1mm 1.5mm; text-align: left; vertical-align: top; }
th { background: #e6e6e6; }
td.r, th.r { text-align: right; }
.sum td { font-weight: bold; }
.groups { display: flex; gap: 8mm; }
.groups > div { flex: 1; }
.sign { margin-top: 12mm; display: flex; gap: 30mm; }
.sign div { border-top: 0.5pt solid #000; padding-top: 1mm; width: 60mm; }
.no-print { margin-bottom: 4mm; }
</style></head><body>
<div class="no-print"><button onclick="window.print()">${esc(t('Ispiši', 'Print'))}</button></div>
<h1>Plan &amp; Generate ${esc(run.mode.toUpperCase())} · ${esc(run.public_code)}</h1>
<div class="meta">${esc(t('Status', 'Status'))}: ${esc(status)} · ${esc(t('Generirano', 'Generated'))}: ${esc(formatDate(run.generated_at, locale))} ${esc(run.generated_at.slice(11, 16))}${run.created_by_name ? ` · ${esc(run.created_by_name)}` : ''}${run.note ? ` · ${esc(run.note)}` : ''}</div>
<table><tbody><tr>
<td>${esc(t('Redaka', 'Lines'))}: <b>${v.lines}</b></td>
<td>${esc(t('Fino (g)', 'Fine (g)'))}: <b>${esc(n(v.fine_g))}</b></td>
<td>${esc(pg2 ? t('Vrijednost po zadnjoj nabavnoj', 'Value at last purchase') : t('Knjigovodstvena vrijednost', 'Book value'))}: <b>${esc(eur(v.total_eur))}</b></td>
${v.spot_value_eur !== null ? `<td>${esc(t('Po spotu', 'At spot'))}: <b>${esc(eur(v.spot_value_eur))}</b></td>` : ''}${v.premium_eur !== null ? `<td>${esc(t('Razlika prema spotu', 'Difference to spot'))}: <b>${esc(eur(v.premium_eur))}</b></td>` : ''}
</tr></tbody></table>
${pg2 ? `<div class="groups"><div>${groupHtml(t('Po dobavljaču', 'By supplier'), run.valuation.by_supplier.map((g) => ({ ...g, label: g.supplier_name ?? String(g.supplier_partner_id ?? '-') })))}</div><div>${groupHtml(t('Po poslovnici', 'By branch'), run.valuation.by_warehouse.map((g) => ({ ...g, label: g.warehouse_code ?? '-' })))}</div></div>` : ''}
<h3>${esc(t('Stavke', 'Lines'))}</h3>
<table><thead><tr>${head.map((h, i) => `<th${numCols.includes(i) ? ' class="r"' : ''}>${esc(h)}</th>`).join('')}</tr></thead>
<tbody>${body.map((r) => `<tr>${r.map((c, i) => `<td${numCols.includes(i) ? ' class="r"' : ''}>${esc(c)}</td>`).join('')}</tr>`).join('')}</tbody></table>
<div class="sign"><div>${esc(t('Pripremio', 'Prepared by'))}</div><div>${esc(t('Odobrio', 'Approved by'))}</div></div>
</body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
}
